import React from 'react'
import classNames from 'classnames'
import { i18n } from '@/locale/i18n'
import {
  formatCryptoPrice,
  getCryptoAssetTicker,
  getCryptoClosePrice,
  getCryptoWindowLabel,
  getPriceToBeat,
  hasCryptoPriceTarget,
} from '@/features/market/utils/cryptoEvent'
import useCryptoCurrentPrice from './useCryptoCurrentPrice'

/**
 * Title line of a crypto price up/down market on list rows:
 * "ETH 1 hour: To beat $2,505.57 | Current: $2,513.32 +$7.75", or "Final" once the close is
 * recorded. Renders nothing without a target. It sits inside the row's title link, so the
 * figures are hidden from the accessible name and one summary names the link instead: the
 * live price repaints every couple of seconds and must not rename a focused link each time.
 */
const CryptoPriceStats = ({ question, className }) => {
  const target = getPriceToBeat(question)
  const current = useCryptoCurrentPrice(question)

  if (!hasCryptoPriceTarget(question)) return null

  const lead = [getCryptoAssetTicker(question), getCryptoWindowLabel(question)].filter(Boolean).join(' ')
  const diff = current === null ? null : current - target
  const above = diff !== null && diff >= 0
  const tone = diff === null ? 'text-Neutral-6' : above ? 'text-positive-4' : 'text-negative-4'
  const currentLabel =
    getCryptoClosePrice(question) === null ? i18n('hardcoded.current') : i18n('question_card.crypto_card_final')
  const signedDiff = diff === null ? '' : `${above ? '+' : '-'}${formatCryptoPrice(Math.abs(diff))}`

  return (
    <span className={classNames('tabular-nums', className)}>
      <span className="sr-only">
        {`${lead}: ${i18n('hardcoded.to_beat')} ${formatCryptoPrice(target)}, ${currentLabel} ${
          current === null ? '—' : `${formatCryptoPrice(current)} ${signedDiff}`
        }`}
      </span>
      <span aria-hidden>
        <span className="text-Neutral-6">{lead}: </span>
        <span className="whitespace-nowrap">
          <span className="font-normal text-Neutral-4">{i18n('hardcoded.to_beat')} </span>
          <span className="text-Neutral-6">{formatCryptoPrice(target)}</span>
        </span>
        <span className="mx-2 font-normal text-Neutral-4">|</span>
        <span className="whitespace-nowrap">
          <span className="font-normal text-Neutral-4">{currentLabel}: </span>
          {current === null ? (
            <span className="text-Neutral-4">—</span>
          ) : (
            <span className={tone}>
              {formatCryptoPrice(current)}
              <span className="ml-1 font-normal">{signedDiff}</span>
            </span>
          )}
        </span>
      </span>
    </span>
  )
}

export default CryptoPriceStats
